import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { IconCopy, IconZelle } from '@belong/icons';
import { Text } from '@belong/ui';
import { clearToasts, showToast } from 'store/redux/toast';

const toastTime = 4000;

type ZelleDetails = {
  recipient: string;
  reason: string;
};

type ZelleDetailsProps = {
  zelle: ZelleDetails;
};

export function ZelleDetails({ zelle }: ZelleDetailsProps) {
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const dispatch = useDispatch();

  const handleCopy = async (text: string, fieldName: string) => {
    if (navigator?.clipboard) {
      try {
        await navigator.clipboard.writeText(text);
        setCopiedField(fieldName);
        setTimeout(() => setCopiedField(null), 2000);
        dispatch(clearToasts());
        dispatch(
          showToast(
            { message: 'Copied to clipboard', created: true },
            { autoDelete: true, autoDeleteTime: toastTime },
          ),
        );
      } catch (err) {
        console.error('Failed to copy text:', err);
      }
    }
  };

  const fields = [
    {
      label: 'Recipient',
      value: zelle.recipient,
      fieldName: 'recipient',
    },
    {
      label: 'Reason',
      value: zelle.reason,
      fieldName: 'reason',
    },
  ];

  return (
    <div className="rounded border border-solid border-gray bg-white">
      {/* Header */}
      <div className="mb-md flex items-center gap-xs rounded-t border-0 border-b border-solid border-gray bg-light-gray p-md">
        <IconZelle className="text-green" width={24} height={24} />
        <Text fontWeight="semibold">Zelle (Instant • Limits May Vary)</Text>
      </div>

      {/* Content */}
      <div className="space-y-lg px-md pb-md md:space-y-xs">
        {fields.map((field) => (
          <div
            key={field.fieldName}
            className="flex items-start justify-between gap-xs"
          >
            <div className="flex min-w-0 flex-1 flex-col md:flex-row md:items-baseline md:gap-xs">
              <Text variant="body" fontWeight="semibold" className="shrink-0">
                {field.label}:
              </Text>
              <Text variant="body" className="break-words">
                {field.value}
              </Text>
            </div>
            <button
              type="button"
              onClick={() => handleCopy(field.value, field.fieldName)}
              className="ml-xs shrink-0 cursor-pointer p-2xs transition-opacity hover:opacity-70"
              aria-label={`Copy ${field.label}`}
              title={
                copiedField === field.fieldName
                  ? 'Copied!'
                  : `Copy ${field.label}`
              }
            >
              <IconCopy className="text-green" width={20} height={20} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
